import { useEffect, useMemo, useRef, useState } from 'react';
import {
  BIOMES,
  BIOME_HEIGHT_PX,
  THEMED_SLOTS,
  WORLD_NODES,
  WORLD_PANEL_WIDTH_PX,
  type WorldMapNode,
  type WorldNodeType,
} from '@goodtime/shared';
import { Avatar } from './Avatar';
import { BiomeBackground, BiomeForeground } from './BiomePanel';
import { buildSvgPath } from './buildPath';
import { EpisodeBanner } from './EpisodeBanner';
import { MapNode } from './MapNode';
import { NodePreview } from './NodePreview';
import { WheelNodePreview } from './WheelNodePreview';
import waypointUrl from '../../assets/map_path/waypoint.png?url';

export type TimeOfDay = 'day' | 'night';

const BANNER_H = 92;
const TOP_PAD = 270;
const BOTTOM_PAD = 120;
const TOTAL_H =
  TOP_PAD +
  BIOMES.length * BIOME_HEIGHT_PX +
  (BIOMES.length - 1) * BANNER_H +
  BOTTOM_PAD;

/** How many nodes ahead of {@code currentNodeId} stay unlocked. Kept
 *  in sync with the v2 map (see {@link
 *  ../v2/WorldMapV2#NODES_VISIBLE_AHEAD}). Bumped from 6 → 12 on
 *  2026-05-12 after the new biome nodes pushed wheel + boss nodes
 *  further along the row. */
const NODES_VISIBLE_AHEAD = 12;

export interface WorldMapViewProps {
  /** Player's current node id (1-indexed; 1..40). */
  currentNodeId: number;
  /** Set of completed node ids. */
  completed: ReadonlySet<number>;
  /** Player taps Play in the preview modal. The host routes this to
   * the matching game scene (or surfaces a placeholder for node types
   * we don't have a scene for yet). */
  onPlayNode: (node: WorldMapNode) => void;
  /** Atmosphere — 'day' shows the warm sky + sun, 'night' overlays a
   * deep-blue multiply tint with twinkling stars. Defaults to 'day'. */
  timeOfDay?: TimeOfDay;
  /** Tap the player avatar marker to open the avatar picker. When omitted the
   *  avatar stays a passive (non-interactive) marker. */
  onOpenAvatarPicker?: () => void;
}

/**
 * Vertical-scrolling Candy-Crush-style world map. 5 biomes stacked
 * top-to-bottom; ~8 nodes per biome on a snaking path; tap a node to
 * open a preview, hit Play to launch that game.
 *
 * <p>Phase W7.1 scope (this commit) — solid sky + hill silhouettes for
 * biome backgrounds. Painterly motif art (castles, palms, etc.) lands
 * in W7.2; bubble friends + day/night toggle in W7.3 alongside save-
 * state wiring through PlayerProfile.
 *
 * <p>Renders absolutely-positioned inside a parent of fixed pixel
 * width (panels are 402px native; on narrow phones the parent should
 * scale-to-fit via CSS transform — same approach the slot scene uses).
 */
/**
 * How fast the background hero scenes scroll relative to the
 * foreground (path + nodes). 0.5 = half-speed, classic parallax depth
 * effect (think PvZ Pirate Seas). 1.0 disables parallax. */
const PARALLAX_BG_SPEED = 0.5;

export function WorldMapView({
  currentNodeId,
  completed,
  onPlayNode,
  timeOfDay = 'day',
  onOpenAvatarPicker,
}: WorldMapViewProps) {
  const [selectedNode, setSelectedNode] = useState<WorldMapNode | null>(null);
  const [scrollTop, setScrollTop] = useState(0);
  const scrollRef = useRef<HTMLDivElement>(null);

  // Compute absolute (x,y) per node within the full vertical canvas.
  const absNodes = useMemo(
    () =>
      WORLD_NODES.map((n) => {
        const biomeOffset = TOP_PAD + n.biome * (BIOME_HEIGHT_PX + BANNER_H);
        return {
          ...n,
          absX: n.x * WORLD_PANEL_WIDTH_PX,
          absY: biomeOffset + n.y * BIOME_HEIGHT_PX,
        };
      }),
    [],
  );

  const pathD = useMemo(
    () => buildSvgPath(absNodes.map((n) => ({ x: n.absX, y: n.absY }))),
    [absNodes],
  );
  // Completed-segment path — same bezier through the first N nodes the
  // player has already cleared. Renders on TOP of the full path with a
  // brighter style so the player can see how far they've come.
  const completedPathD = useMemo(() => {
    const completedNodes = absNodes.filter(
      (n) => completed.has(n.id) || n.id === currentNodeId,
    );
    return buildSvgPath(
      completedNodes.map((n) => ({ x: n.absX, y: n.absY })),
    );
  }, [absNodes, completed, currentNodeId]);

  // Scroll to the current node on mount so the player lands looking at
  // their progression. Offset 500px so the current node is in the
  // lower portion of view (there's "room ahead" visible).
  useEffect(() => {
    const cur = absNodes.find((n) => n.id === currentNodeId);
    if (cur && scrollRef.current) {
      scrollRef.current.scrollTop = Math.max(0, cur.absY - 500);
    }
  }, [absNodes, currentNodeId]);

  const currentAbs = absNodes.find((n) => n.id === currentNodeId);
  const previewBiome = selectedNode ? (BIOMES[selectedNode.biome] ?? null) : null;
  const firstBiome = BIOMES[0]!;
  const lastBiome = BIOMES[BIOMES.length - 1]!;

  return (
    <div
      style={{
        position: 'relative',
        width: '100%',
        height: '100%',
        overflow: 'hidden',
        background: firstBiome.sky[0],
        fontFamily: '"Fredoka", -apple-system, system-ui',
      }}
    >
      {/* keyframes used by MapNode + NodePreview animations */}
      <style>{ANIMATION_KEYFRAMES}</style>

      <div
        ref={scrollRef}
        onScroll={(e) => setScrollTop((e.currentTarget as HTMLDivElement).scrollTop)}
        style={{
          position: 'absolute',
          inset: 0,
          overflowY: 'auto',
          overflowX: 'hidden',
          WebkitOverflowScrolling: 'touch',
          scrollbarWidth: 'none',
        }}
      >
        <div
          style={{
            position: 'relative',
            width: '100%',
            height: TOTAL_H,
          }}
        >
          {/* Top sky pad — extends first biome's top color upward */}
          <div
            style={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              height: TOP_PAD,
              background: firstBiome.sky[0],
            }}
          />
          {/* Bottom sky pad — extends last biome's bottom color downward */}
          <div
            style={{
              position: 'absolute',
              bottom: 0,
              left: 0,
              right: 0,
              height: BOTTOM_PAD,
              background: lastBiome.sky[2],
            }}
          />

          {/* Biome backgrounds (hero scenes / sky+silhouettes). Wrapped
              in a single parallax container that translates at
              PARALLAX_BG_SPEED (0.5) relative to the foreground scroll
              speed — gives the PvZ-style depth illusion. z-index 0
              so they sit BEHIND the path; foreground motifs render
              separately at z-index 11 so they occlude the path. */}
          <div
            style={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              height: TOTAL_H,
              zIndex: 0,
              willChange: 'transform',
              transform: `translateY(${scrollTop * (1 - PARALLAX_BG_SPEED)}px)`,
              pointerEvents: 'none',
            }}
            aria-hidden
          >
            {BIOMES.map((b, i) => {
              const offset = TOP_PAD + i * (BIOME_HEIGHT_PX + BANNER_H);
              return (
                <div
                  key={`bg-${b.id}`}
                  style={{
                    position: 'absolute',
                    top: offset,
                    left: 0,
                    right: 0,
                  }}
                >
                  <BiomeBackground
                    biome={b}
                    idx={i}
                    prevBiome={i > 0 ? (BIOMES[i - 1] ?? null) : null}
                    nextBiome={BIOMES[i + 1] ?? null}
                  />
                </div>
              );
            })}
          </div>

          {/* Night atmosphere — twinkling stars layer + multiply-mode
              dark-blue tint. Both render at z 6/7 so they sit BELOW
              the path (z 10), nodes (z 20+), and avatar but ABOVE the
              biome panels. */}
          {timeOfDay === 'night' && (
            <>
              <svg
                width="100%"
                height={TOTAL_H}
                viewBox={`0 0 ${WORLD_PANEL_WIDTH_PX} ${TOTAL_H}`}
                preserveAspectRatio="none"
                style={{
                  position: 'absolute',
                  inset: 0,
                  pointerEvents: 'none',
                  zIndex: 6,
                }}
              >
                {Array.from({ length: 80 }).map((_, i) => (
                  <circle
                    key={i}
                    cx={(i * 47) % WORLD_PANEL_WIDTH_PX}
                    cy={(i * 131) % TOTAL_H}
                    r={(i % 3) * 0.5 + 0.7}
                    fill="#fff"
                    opacity={0.3 + (i % 5) * 0.12}
                  />
                ))}
              </svg>
              <div
                style={{
                  position: 'absolute',
                  inset: 0,
                  zIndex: 7,
                  background:
                    'linear-gradient(180deg, rgba(20,10,50,0.55), rgba(40,20,80,0.4))',
                  pointerEvents: 'none',
                  mixBlendMode: 'multiply',
                }}
              />
            </>
          )}

          {/* Path overlay — full-canvas SVG. Cobblestone tile.png fills
              the path edge-to-edge via SVG pattern (no padding around
              the tile); vector-effect=non-scaling-stroke keeps the
              stroke crisp at any viewport width. SVG turbulence filter
              applies a subtle rustic wobble to the path edge so it
              doesn't read as a perfect bezier curve. Completed segments
              render on top at full opacity; upcoming segments are
              paler so the player can see how far they've come. */}
          <svg
            width="100%"
            height={TOTAL_H}
            viewBox={`0 0 ${WORLD_PANEL_WIDTH_PX} ${TOTAL_H}`}
            preserveAspectRatio="none"
            style={{
              position: 'absolute',
              inset: 0,
              pointerEvents: 'none',
              zIndex: 10,
            }}
          >
            {/* Path — thin painterly white curving line connecting
                node platforms (PvZ Pirate Seas style). Soft glow
                outline below for grounding; bright white core on top.
                Upcoming segments at lower opacity; completed at full. */}
            <path
              d={pathD}
              fill="none"
              stroke="rgba(255,255,255,0.35)"
              strokeWidth="9"
              strokeLinecap="round"
              strokeLinejoin="round"
              vectorEffect="non-scaling-stroke"
              filter="drop-shadow(0 2px 4px rgba(0,0,0,0.35))"
            />
            <path
              d={pathD}
              fill="none"
              stroke="rgba(255,255,255,0.7)"
              strokeWidth="4.5"
              strokeLinecap="round"
              strokeLinejoin="round"
              vectorEffect="non-scaling-stroke"
            />
            {/* Completed segment — bright bold white core */}
            {completedPathD && (
              <>
                <path
                  d={completedPathD}
                  fill="none"
                  stroke="rgba(255,255,255,0.55)"
                  strokeWidth="9"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  vectorEffect="non-scaling-stroke"
                />
                <path
                  d={completedPathD}
                  fill="none"
                  stroke="#FFFFFF"
                  strokeWidth="4.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  vectorEffect="non-scaling-stroke"
                />
              </>
            )}
          </svg>

          {/* Biome foregrounds (motifs + tufts) at z-index 11 so trees /
              cottages / cacti / neon signs occlude the path where they
              overlap, giving real depth. */}
          {BIOMES.map((b, i) => {
            const offset = TOP_PAD + i * (BIOME_HEIGHT_PX + BANNER_H);
            return (
              <div
                key={`fg-${b.id}`}
                style={{
                  position: 'absolute',
                  top: offset,
                  left: 0,
                  right: 0,
                  zIndex: 11,
                  pointerEvents: 'none',
                }}
              >
                <BiomeForeground
                  biome={b}
                  idx={i}
                  prevBiome={i > 0 ? (BIOMES[i - 1] ?? null) : null}
                  nextBiome={BIOMES[i + 1] ?? null}
                />
              </div>
            );
          })}

          {/* Episode banners — first biome's banner over the top pad,
              and one banner per biome seam. Sit at z-index 20 above
              foreground motifs and the path. */}
          {BIOMES.map((b, i) => {
            const offset = TOP_PAD + i * (BIOME_HEIGHT_PX + BANNER_H);
            const nextBiome = BIOMES[i + 1] ?? null;
            return (
              <div
                key={`banner-${b.id}`}
                style={{ position: 'absolute', top: offset, left: 0, right: 0 }}
              >
                {i === 0 && (
                  <div
                    style={{
                      position: 'absolute',
                      top: -BANNER_H - 16,
                      left: 0,
                      right: 0,
                      pointerEvents: 'none',
                      zIndex: 20,
                    }}
                  >
                    <EpisodeBanner biome={b} />
                  </div>
                )}
                {nextBiome && (
                  <div
                    style={{
                      position: 'absolute',
                      top: BIOME_HEIGHT_PX,
                      left: 0,
                      right: 0,
                      height: BANNER_H,
                      zIndex: 20,
                    }}
                  >
                    <EpisodeBanner biome={nextBiome} />
                  </div>
                )}
              </div>
            );
          })}

          {/* Waypoint marker — commissioned glowing star marker placed
              under the current node. Sits below MapNode (z-index 20+)
              so the coin button is on top, but above the path glow. */}
          {currentAbs && (
            <img
              src={waypointUrl}
              alt=""
              draggable={false}
              style={{
                position: 'absolute',
                left: `${(currentAbs.x * 100).toFixed(2)}%`,
                top: currentAbs.absY,
                transform: 'translate(-50%, -50%)',
                width: 96,
                height: 96,
                pointerEvents: 'none',
                zIndex: 19,
                animation: 'gtPulse 1.6s ease-out infinite',
              }}
            />
          )}

          {/* Nodes — positioned via percent X so they ride the responsive
              panel width. */}
          {absNodes.map((n) => (
            <MapNode
              key={n.id}
              node={n}
              absX={`${(n.x * 100).toFixed(2)}%`}
              absY={n.absY}
              isCurrent={n.id === currentNodeId}
              isCompleted={completed.has(n.id)}
              isLocked={
                // Wheel nodes are the multiplayer entry — never
                // lock them on slot progression. See the matching
                // exception in WorldMapV2.
                n.type !== 'wheel' &&
                (n.id > currentNodeId + NODES_VISIBLE_AHEAD ||
                  n.type === 'locked')
              }
              onClick={(node) => setSelectedNode(node)}
            />
          ))}

          {/* Avatar on current node */}
          {currentAbs && (
            <div
              style={{
                position: 'absolute',
                left: `${(currentAbs.x * 100).toFixed(2)}%`,
                top: currentAbs.absY - 32,
                transform: 'translate(-50%, -100%)',
                zIndex: 31,
                pointerEvents: onOpenAvatarPicker ? 'auto' : 'none',
                animation: 'gtIdle 2.4s ease-in-out infinite',
              }}
            >
              <Avatar onPress={onOpenAvatarPicker} />
            </div>
          )}
        </div>
      </div>

      {/* Preview modal — wheel rooms get the how-it-works dialogue;
          every other node uses the standard candy-coin preview. */}
      {selectedNode && previewBiome && selectedNode.type === 'wheel' && (
        <WheelNodePreview
          node={selectedNode}
          biome={previewBiome}
          onClose={() => setSelectedNode(null)}
          onEnter={(node) => onPlayNode(node)}
        />
      )}
      {selectedNode && previewBiome && selectedNode.type !== 'wheel' && (
        <NodePreview
          node={selectedNode}
          biome={previewBiome}
          onClose={() => setSelectedNode(null)}
          onPlay={(node) => onPlayNode(node)}
        />
      )}
    </div>
  );
}

/** Maps a world-map node type to one of the engine's playable scenes.
 * Returns null for node types we don't have a scene for yet —
 * tournament bosses, scratch tickets, free-spin chests, daily quests,
 * card games. The host can fall back to a placeholder or the slot
 * scene until those games ship. */
export function nodeTypeToScene(type: WorldNodeType): string | null {
  // Themed Kingdom-biome slot variants all surface as the slot scene —
  // they re-skin the reels via a theme registry switch wired at
  // scene-mount time (App.tsx → canvasSceneThemeId). Checked from the
  // THEMED_SLOTS registry so new themed slots route automatically.
  if (type in THEMED_SLOTS) return 'slot';
  switch (type) {
    case 'slot':
      return 'slot';
    case 'bingo':
      return 'bingo';
    case 'mini':
      // Pick one — Plinko reads as the most "mini-game-y" of the four.
      return 'plinko';
    case 'card':
      // Higher-Lower is the closest engine-side card game.
      return 'higherlower';
    case 'boss':
      // Tournaments don't exist yet — surface as Crash for now (high-
      // tension, leaderboard-shaped). Replace when tournaments land.
      return 'crash';
    case 'scratch':
      return 'scratch';
    case 'chest':
      return 'chest';
    case 'daily':
      return 'daily';
    case 'wheel':
    // Wheel rooms have their own React-mode lobby (cast view); the
    // App routes to it directly on node type rather than via a
    // CanvasHost scene. Returning null keeps {@code handlePlayWorldNode}
    // from forcing a 'game' view for these.
    // falls through
    case 'locked':
    default:
      return null;
  }
}

const ANIMATION_KEYFRAMES = `
  @keyframes gtPulse {
    0%   { transform: scale(0.9); opacity: 0.9; }
    70%  { transform: scale(1.45); opacity: 0; }
    100% { transform: scale(1.45); opacity: 0; }
  }
  @keyframes gtFloat {
    0%, 100% { transform: translate(-50%, -50%) translateY(0); }
    50%      { transform: translate(-50%, -50%) translateY(-4px); }
  }
  @keyframes gtIdle {
    0%, 100% { transform: translate(-50%, -100%) translateY(0); }
    50%      { transform: translate(-50%, -100%) translateY(-2px); }
  }
  @keyframes gtFloatBubble {
    0%, 100% { transform: translateY(0); }
    50%      { transform: translateY(-6px); }
  }
  @keyframes gtFadeIn {
    from { opacity: 0; }
    to   { opacity: 1; }
  }
  @keyframes gtPop {
    0%   { transform: scale(0.7); opacity: 0; }
    100% { transform: scale(1);   opacity: 1; }
  }
`;
